/*
 * Lemonade Training Oy, copy rights, all right reserved.
 */
import React, { useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "@lemonadetraining/web-ui";

import { SAFE_ACTION_LABEL } from "./copy";

interface CancelConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What will actually happen. Announced with the dialog, not on request. */
  consequence: string;
  confirmLabel: string;
  onConfirm: () => void;
  loading?: boolean;
  testId?: string;
}

/**
 * The destructive confirm for this flow.
 *
 * `role="alertdialog"` rather than the shadcn default: the registry ships no
 * alert-dialog, and a plain `dialog` does not guarantee the description is
 * announced on open — which is where the consequence lives. Radix already
 * points `aria-describedby` at the description, so it is announced with the
 * title.
 *
 * Focus opens on the SAFE action, and the close "✕" is dropped so that the
 * confirming action is EXACTLY one Tab away. That distance is deliberate: on
 * the statutory lane the confirming action is the athlete exercising a legal
 * right, not a mistake to be guarded against, so it gets a safe default and
 * nothing more — no timer, no hold-to-confirm, no second confirm.
 */
const CancelConfirmDialog: React.FC<CancelConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel,
  onConfirm,
  loading = false,
  testId = "cancel-confirm-dialog",
}) => {
  const safeActionRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        role="alertdialog"
        showCloseButton={false}
        data-testid={testId}
        className="max-w-[min(30rem,calc(100%-2rem))]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          safeActionRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="text-foreground" data-testid="cancel-confirm-consequence">
            {consequence}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            ref={safeActionRef}
            variant="outline"
            className="min-h-[44px] px-4"
            onClick={() => onOpenChange(false)}
            data-testid="cancel-confirm-safe"
          >
            {SAFE_ACTION_LABEL}
          </Button>
          {/* Not the shared `destructive` variant: it computes 3.05:1 in the
              light theme and fails AA. Severity is carried by the alertdialog
              description and the label, never by colour. */}
          <Button
            className="min-h-[44px] px-4"
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading}
            data-testid="cancel-confirm-action"
          >
            {loading && <Spinner className="h-4 w-4" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelConfirmDialog;
